"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  ChevronRight,
  Cloud,
  Download,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  History,
  Info,
  LoaderCircle,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  Workflow,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRunyiBackTrail } from "@/lib/use-runyi-back-trail";
import { useRunyiUiProjection } from "./runyi-ui-coordinator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Account = {
  user_id: string | null;
  email: string;
  role: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  active: boolean;
};

type StorageObject = {
  id: string | null;
  name: string;
  created_at?: string;
  updated_at?: string;
  last_accessed_at?: string;
  metadata?: Record<string, unknown> | null;
};

type CapabilityState = "AVAILABLE" | "REQUIRES_APPROVAL" | "READ_ONLY" | "NOT_CONNECTED" | "UNSUPPORTED";

type WorkspaceCapability = {
  key: string;
  state: CapabilityState;
  action_key?: string | null;
  source?: string | null;
  metadata?: Record<string, unknown> | null;
};

type ProviderBinding = {
  purpose: string;
  provider: string;
  adapter: string;
  bucket?: string | null;
  base_path?: string | null;
  state: string;
  read_enabled: boolean;
  write_enabled: boolean;
  presign_enabled: boolean;
  metadata?: Record<string, unknown> | null;
};

type Workspace = {
  workspace_key: string;
  workspace_id: string;
  parent_workspace_key?: string | null;
  workspace_type: string;
  display_name: string;
  description: string;
  lifecycle_state: string;
  visibility: string;
  tenant_user_id?: string | null;
  tenant_project_ref?: string | null;
  default_provider_key?: string | null;
  member_role?: string | null;
  metadata?: Record<string, unknown> | null;
  capabilities: WorkspaceCapability[];
  provider_bindings: ProviderBinding[];
};

type WorkspaceCatalogue = {
  architecture?: string;
  role?: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  selected_account_user_id?: string | null;
  selected_account_email?: string | null;
  default_workspace_key?: string | null;
  projection_state?: string | null;
  workspaces?: Workspace[];
};

type Surface = "all" | "files" | "infrastructure" | "apps" | "recent";

type SystemObject = {
  entity_key: string;
  entity_type_key: string;
  display_name: string;
  domain: string;
  tenant_project_ref?: string | null;
  environment_key?: string | null;
  lifecycle_state?: string | null;
  health_state?: string | null;
  identity_email?: string | null;
  identity_email_type?: string | null;
  identity_email_verified?: boolean | null;
  identity_email_source?: string | null;
  sync_state?: string | null;
  sync_error?: string | null;
  last_synced_at?: string | null;
  updated_at?: string | null;
  attributes?: Record<string, unknown> | null;
};

type UniversalSearchPayload = {
  account_user_id?: string | null;
  account_email?: string | null;
  surface?: string;
  query?: string;
  total?: number;
  items?: SystemObject[];
  limit?: number;
  scope?: { role?: string; tenant_project_refs?: string[]; direct_registry_access?: boolean };
};

const PAGE_SIZE = 50;
const MAX_SUPABASE_UPLOAD_BYTES = 25 * 1024 * 1024;
const EXPLORER_UI_CACHE_KEY = "runyi:ui:explorer:v1";

type ExplorerUiCache = {
  accounts?: Account[];
  items?: SystemObject[];
  total?: number | null;
  accountEmail?: string | null;
  catalogue?: WorkspaceCatalogue | null;
  selectedWorkspaceKey?: string | null;
  storageListing?: {
    workspaceKey: string;
    items: StorageObject[];
    hasMore: boolean;
    nextOffset: number;
  } | null;
};

function readExplorerUiCache(): ExplorerUiCache | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(EXPLORER_UI_CACHE_KEY);
    return raw ? JSON.parse(raw) as ExplorerUiCache : null;
  } catch { return null; }
}

function writeExplorerUiCache(value: Partial<ExplorerUiCache>) {
  if (typeof window === "undefined") return;
  try {
    const current = readExplorerUiCache() ?? {};
    window.localStorage.setItem(EXPLORER_UI_CACHE_KEY, JSON.stringify({ ...current, ...value }));
  } catch { /* best effort */ }
}

function joinPath(...parts: Array<string | null | undefined>) {
  return parts.filter(Boolean).join("/").replace(/\/{2,}/g, "/").replace(/^\/+|\/+$/g, "");
}

function formatBytes(value: unknown) {
  const bytes = typeof value === "number" ? value : Number(value ?? 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatDate(value?: string) {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}

function isFolder(object: StorageObject) {
  return object.id === null && !object.metadata;
}

function objectType(object: StorageObject) {
  if (isFolder(object)) return "Folder";
  const mime = String(object.metadata?.mimetype ?? object.metadata?.contentType ?? "");
  return mime || object.name.split(".").pop()?.toUpperCase() || "File";
}

function objectSize(object: StorageObject) {
  return Number(object.metadata?.size ?? 0);
}

function capability(workspace: Workspace | null, key: string) {
  return workspace?.capabilities?.find((item) => item.key === key) ?? null;
}

function capabilityEnabled(workspace: Workspace | null, key: string) {
  return capability(workspace, key)?.state === "AVAILABLE";
}

function capabilityBadge(state: CapabilityState) {
  if (state === "AVAILABLE") return "border-emerald-500/25 bg-emerald-500/10 text-emerald-500";
  if (state === "REQUIRES_APPROVAL") return "border-amber-500/25 bg-amber-500/10 text-amber-500";
  if (state === "READ_ONLY") return "border-sky-500/25 bg-sky-500/10 text-sky-500";
  return "border-foreground/10 bg-foreground/[0.035] text-foreground/40";
}

function routeForSystemObject(item: SystemObject): "files" | "infrastructure" | "applications" | "governance" | "new" {
  const type = String(item.entity_type_key || "").toUpperCase();
  const domain = String(item.domain || "").toUpperCase();
  if (type === "WORKSPACE" || type.includes("FILE") || domain.includes("WORKSPACE") || domain.includes("FILE")) return "files";
  if (type === "APPLICATION" || type.includes("INFRASTRUCTURE_PROJECT") || domain.includes("APPLICATION")) return "applications";
  if (domain.includes("SETTING") || domain.includes("GOVERNANCE")) return "governance";
  if (type.includes("SETUP") || domain.includes("ACQUISITION")) return "new";
  return "infrastructure";
}


export function UniversalFileExplorer({
  supabase,
  connected,
  isAdmin,
  role,
  onNavigate,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  isAdmin: boolean;
  role?: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  onNavigate: (route: "ai" | "bridge" | "files" | "infrastructure" | "applications" | "governance" | "new") => void;
}) {
  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [accounts, setAccounts] = useState<Account[]>(() => readExplorerUiCache()?.accounts ?? []);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(() => readExplorerUiCache()?.catalogue?.selected_account_user_id ?? null);
  const [catalogue, setCatalogue] = useState<WorkspaceCatalogue | null>(() => readExplorerUiCache()?.catalogue ?? null);
  const [selectedWorkspaceKey, setSelectedWorkspaceKey] = useState<string | null>(() => readExplorerUiCache()?.selectedWorkspaceKey ?? readExplorerUiCache()?.catalogue?.default_workspace_key ?? null);
  const [surface, setSurface] = useState<Surface>("all");
  const [path, setPath] = useState("");
  const [query, setQuery] = useState("");
  const [objects, setObjects] = useState<StorageObject[]>(() => {
    const cached = readExplorerUiCache();
    const selectedKey = cached?.selectedWorkspaceKey ?? cached?.catalogue?.default_workspace_key ?? null;
    return cached?.storageListing?.workspaceKey === selectedKey ? cached.storageListing.items : [];
  });
  const [selected, setSelected] = useState<StorageObject | null>(null);
  const [offset, setOffset] = useState(() => {
    const cached = readExplorerUiCache();
    const selectedKey = cached?.selectedWorkspaceKey ?? cached?.catalogue?.default_workspace_key ?? null;
    return cached?.storageListing?.workspaceKey === selectedKey ? cached.storageListing.nextOffset : 0;
  });
  const [hasMore, setHasMore] = useState(() => {
    const cached = readExplorerUiCache();
    const selectedKey = cached?.selectedWorkspaceKey ?? cached?.catalogue?.default_workspace_key ?? null;
    return cached?.storageListing?.workspaceKey === selectedKey ? cached.storageListing.hasMore : false;
  });
  const [loading, setLoading] = useState(false);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [resultsExpanded, setResultsExpanded] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [systemObjects,setSystemObjects] = useState<SystemObject[]>(() => readExplorerUiCache()?.items ?? []);
  const [systemTotal,setSystemTotal] = useState<number | null>(() => readExplorerUiCache()?.total ?? null);
  const [systemAccountEmail,setSystemAccountEmail] = useState<string | null>(() => readExplorerUiCache()?.accountEmail ?? null);
  const [systemLoading,setSystemLoading] = useState(false);
  const [systemError,setSystemError] = useState<string | null>(null);
  const uiProjection = useRunyiUiProjection({ supabase, connected, screenKey: "EXPLORER" });
  const restoringWorkspaceRef = useRef(false);
  const objectCacheRef = useRef(new Map<string,{ items: StorageObject[]; hasMore: boolean; nextOffset: number }>());
  const accountsRef = useRef(accounts);
  useEffect(() => { accountsRef.current = accounts; }, [accounts]);

  useEffect(() => {
    const payload = (uiProjection?.payload ?? {}) as Record<string, any>;
    const nextAccounts = Array.isArray(payload.accounts) ? payload.accounts as Account[] : null;
    const nextItems = Array.isArray(payload.workspace_items) ? payload.workspace_items as SystemObject[] : null;
    const nextCatalogue = payload.workspace_catalogue && typeof payload.workspace_catalogue === "object"
      ? payload.workspace_catalogue as WorkspaceCatalogue
      : null;
    const nextStorageListing = payload.storage_listing && typeof payload.storage_listing === "object"
      ? payload.storage_listing as Record<string, any>
      : null;

    if (nextAccounts) {
      accountsRef.current = nextAccounts;
      setAccounts(nextAccounts);
    }
    if (nextCatalogue) {
      setCatalogue(nextCatalogue);
      setSelectedAccount(nextCatalogue.selected_account_user_id ?? null);
      if (nextCatalogue.selected_account_email) setSystemAccountEmail(nextCatalogue.selected_account_email);
      const available = nextCatalogue.workspaces ?? [];
      setSelectedWorkspaceKey((current) => {
        if (current && available.some((item) => item.workspace_key === current)) return current;
        return nextCatalogue.default_workspace_key ?? available[0]?.workspace_key ?? null;
      });
    }
    if (nextItems && !query.trim() && surface === "all") {
      setSystemObjects(nextItems);
      setSystemTotal(nextItems.length);
    }

    let projectedStorageCache: ExplorerUiCache["storageListing"] = null;
    if (nextStorageListing && Array.isArray(nextStorageListing.items)) {
      const storageWorkspaceKey = String(nextStorageListing.workspace_key ?? "");
      const storageItems = nextStorageListing.items as StorageObject[];
      const storageNextOffset = typeof nextStorageListing.next_offset === "number"
        ? nextStorageListing.next_offset
        : storageItems.length;
      const storageHasMore = Boolean(nextStorageListing.has_more);

      if (storageWorkspaceKey) {
        projectedStorageCache = {
          workspaceKey: storageWorkspaceKey,
          items: storageItems,
          hasMore: storageHasMore,
          nextOffset: storageNextOffset,
        };
        objectCacheRef.current.set(
          [storageWorkspaceKey, "", "", "all"].join("|"),
          { items: storageItems, hasMore: storageHasMore, nextOffset: storageNextOffset },
        );
        objectCacheRef.current.set(
          [storageWorkspaceKey, "", "", "files"].join("|"),
          { items: storageItems, hasMore: storageHasMore, nextOffset: storageNextOffset },
        );

        const rootView = !path && !query.trim() && (surface === "all" || surface === "files");
        const selectedMatches = !selectedWorkspaceKey || selectedWorkspaceKey === storageWorkspaceKey;
        if (rootView && selectedMatches) {
          setObjects(storageItems);
          setOffset(storageNextOffset);
          setHasMore(storageHasMore);
        }
      }
    }

    if (nextAccounts || nextItems || nextCatalogue || projectedStorageCache) {
      writeExplorerUiCache({
        accounts: nextAccounts ?? accountsRef.current,
        items: nextItems ?? readExplorerUiCache()?.items ?? [],
        total: nextItems ? nextItems.length : readExplorerUiCache()?.total ?? null,
        accountEmail: nextCatalogue?.selected_account_email ?? readExplorerUiCache()?.accountEmail ?? null,
        catalogue: nextCatalogue ?? readExplorerUiCache()?.catalogue ?? null,
        storageListing: projectedStorageCache ?? readExplorerUiCache()?.storageListing ?? null,
      });
    }
  }, [uiProjection, path, query, selectedWorkspaceKey, surface]);

  useEffect(() => {
    writeExplorerUiCache({ catalogue, selectedWorkspaceKey });
  }, [catalogue, selectedWorkspaceKey]);

  useEffect(() => {
    const onBack = (event: Event) => {
      if (event.defaultPrevented) return;
      if (createOpen) { event.preventDefault(); setCreateOpen(false); return; }
      if (menuOpen) { event.preventDefault(); setMenuOpen(false); return; }
      if (scopeOpen) { event.preventDefault(); setScopeOpen(false); return; }
      if (selected) { event.preventDefault(); setSelected(null); }
    };
    window.addEventListener("runyi:request-back", onBack);
    return () => window.removeEventListener("runyi:request-back", onBack);
  }, [createOpen, menuOpen, scopeOpen, selected]);

  useRunyiBackTrail({ selectedWorkspaceKey, surface, path }, (previous) => {
    restoringWorkspaceRef.current = previous.selectedWorkspaceKey !== selectedWorkspaceKey;
    setSelectedWorkspaceKey(previous.selectedWorkspaceKey);
    setSurface(previous.surface);
    setPath(previous.path);
    setSelected(null);
  });


  const workspaces = catalogue?.workspaces ?? [];
  const selectedWorkspace = useMemo(
    () => workspaces.find((item) => item.workspace_key === selectedWorkspaceKey) ?? null,
    [workspaces, selectedWorkspaceKey],
  );
  const activeBinding = useMemo(
    () => selectedWorkspace?.provider_bindings?.find((item) => item.purpose === "PRIMARY_STORAGE" && item.state === "ACTIVE" && item.read_enabled) ?? null,
    [selectedWorkspace],
  );
  const browseConnected = Boolean(
    activeBinding && activeBinding.provider === "SUPABASE" && activeBinding.adapter === "SUPABASE_STORAGE" && capabilityEnabled(selectedWorkspace, "FILE_BROWSE"),
  );
  const uploadEnabled = Boolean(browseConnected && activeBinding?.write_enabled && capabilityEnabled(selectedWorkspace, "FILE_UPLOAD"));
  const downloadEnabled = Boolean(browseConnected && activeBinding?.presign_enabled && capabilityEnabled(selectedWorkspace, "FILE_DOWNLOAD"));


  const loadScope = useCallback(async (preferredAccount?: string | null) => {
    if (!supabase || !connected) return;
    setScopeLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-ui-gateway", {
        body: { screen: "EXPLORER", mode: "explorer_scope", account_user_id: preferredAccount ?? null },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      const next = (data ?? {}) as WorkspaceCatalogue;
      const available = next.workspaces ?? [];
      setCatalogue(next);
      setSelectedAccount(next.selected_account_user_id ?? preferredAccount ?? null);
      if (next.selected_account_email) setSystemAccountEmail(next.selected_account_email);
      setSelectedWorkspaceKey((current) => {
        if (current && available.some((item) => item.workspace_key === current)) return current;
        return next.default_workspace_key ?? available[0]?.workspace_key ?? null;
      });
      writeExplorerUiCache({ catalogue: next, accountEmail: next.selected_account_email ?? null });
      setNotice(null);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Unable to update workspace scope. Last valid state is retained.");
    } finally {
      setScopeLoading(false);
    }
  }, [connected, supabase]);

  const listObjects = useCallback(async (reset = true) => {
    if (!supabase || !connected || !selectedWorkspace || !browseConnected) {
      if (!selectedWorkspace || !browseConnected) {
        setObjects([]);
        setHasMore(false);
        setOffset(0);
      }
      return;
    }

    const nextOffset = reset ? 0 : offset;
    const cacheKey = [selectedWorkspace.workspace_key, path, query.trim(), surface].join("|");
    if (reset) {
      const cached = objectCacheRef.current.get(cacheKey);
      if (cached) {
        setObjects(cached.items);
        setHasMore(cached.hasMore);
        setOffset(cached.nextOffset);
      }
    }

    setLoading(!reset || !objectCacheRef.current.has(cacheKey));
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-ui-gateway", {
        body: {
          screen: "EXPLORER",
          mode: "explorer_browse",
          workspace_key: selectedWorkspace.workspace_key,
          path,
          query: query.trim(),
          surface: surface === "recent" ? "RECENT" : "FILES",
          limit: PAGE_SIZE,
          offset: nextOffset,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      if (String(data?.projection_state ?? "READY").toUpperCase() === "UNAVAILABLE") {
        throw new Error(String(data?.reason ?? "Workspace listing is temporarily unavailable."));
      }
      const next = Array.isArray(data?.items) ? data.items as StorageObject[] : [];
      const nextCursor = typeof data?.next_offset === "number" ? data.next_offset : nextOffset + next.length;
      const nextHasMore = Boolean(data?.has_more);
      if (reset) {
        setObjects(next);
        objectCacheRef.current.set(cacheKey,{ items: next, hasMore: nextHasMore, nextOffset: nextCursor });
      } else {
        setObjects((current) => {
          const merged = [...current, ...next];
          objectCacheRef.current.set(cacheKey,{ items: merged, hasMore: nextHasMore, nextOffset: nextCursor });
          return merged;
        });
      }
      setOffset(nextCursor);
      setHasMore(nextHasMore);
      if (reset) setSelected(null);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Unable to update workspace objects. Last valid state is retained.");
    } finally {
      setLoading(false);
    }
  }, [browseConnected, connected, offset, path, query, selectedWorkspace, supabase, surface]);

  useEffect(() => {
    if (restoringWorkspaceRef.current) {
      restoringWorkspaceRef.current = false;
      setOffset(0);
      setSelected(null);
      return; // The Back controller restores the exact previous folder path.
    }
    setPath("");
    setOffset(0);
    setSelected(null);
  }, [selectedWorkspaceKey]);
  useEffect(() => {
    setResultsExpanded(surface !== "files" && surface !== "recent");
  }, [surface]);
  useEffect(() => {
    const timer = window.setTimeout(() => { void listObjects(true); }, 120);
    return () => window.clearTimeout(timer);
  }, [selectedWorkspaceKey, path, query, surface]);

  const loadSystemObjects = useCallback(async () => {
    if (!supabase || !connected) {
      setSystemObjects([]);
      setSystemTotal(null);
      setSystemAccountEmail(null);
      setSystemError(null);
      return;
    }
    if (!query.trim() && surface === "all") {
      setSystemError(null);
      return;
    }
    setSystemLoading(true);
    setSystemError(null);
    try {
      const { data,error } = await supabase.rpc("runyi_universal_workspace_search", {
        p_account_user_id: selectedAccount,
        p_query: query.trim(),
        p_surface: surface === "files" ? "FILES"
          : surface === "infrastructure" ? "INFRASTRUCTURE"
          : surface === "apps" ? "APPS"
          : surface === "recent" ? "RECENT" : "ALL",
        p_limit: 100,
      });
      if (error) throw error;
      const payload=(data ?? {}) as UniversalSearchPayload;
      const nextItems=payload.items ?? [];
      setSystemObjects(nextItems);
      setSystemTotal(typeof payload.total === "number" ? payload.total : nextItems.length);
      setSystemAccountEmail(payload.account_email ?? null);
      writeExplorerUiCache({ accounts: accountsRef.current, items: nextItems, total: typeof payload.total === "number" ? payload.total : nextItems.length, accountEmail: payload.account_email ?? null });
    } catch (error) {
      setSystemError(error instanceof Error ? error.message : "Universal account results could not be refreshed. Last valid state is retained.");
    } finally {
      setSystemLoading(false);
    }
  },[connected,query,selectedAccount,supabase,surface]);

  useEffect(() => {
    const timer=window.setTimeout(() => { void loadSystemObjects(); },120);
    return () => window.clearTimeout(timer);
  },[loadSystemObjects]);

  async function download(object: StorageObject) {
    if (!supabase || !activeBinding?.bucket || !downloadEnabled || isFolder(object)) return;
    try {
      const objectPath = joinPath(activeBinding.base_path, path, object.name);
      const { data, error } = await supabase.storage.from(activeBinding.bucket).createSignedUrl(objectPath, 60, { download: object.name });
      if (error) throw error;
      window.location.assign(data.signedUrl);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Secure download failed.");
    }
  }

  async function uploadFiles(files: FileList | null) {
    if (!supabase || !activeBinding?.bucket || !uploadEnabled || !files?.length) return;
    setUploading(true);
    setNotice(null);
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_SUPABASE_UPLOAD_BYTES) throw new Error(`${file.name} exceeds the current 25 MB Supabase workspace upload boundary.`);
        const objectPath = joinPath(activeBinding.base_path, path, file.name);
        const { error } = await supabase.storage.from(activeBinding.bucket).upload(objectPath, file, { upsert: false, contentType: file.type || undefined });
        if (error) throw error;
      }
      setNotice(`${files.length} file${files.length === 1 ? "" : "s"} uploaded to ${selectedWorkspace?.display_name ?? "workspace"}.`);
      await listObjects(true);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Upload failed.");
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  }

  async function createWorkspace() {
    if (!supabase || !newWorkspaceName.trim()) return;
    setCreatingWorkspace(true);
    setNotice(null);
    try {
      const { data, error } = await supabase.rpc("runyi_workspace_create", {
        p_display_name: newWorkspaceName.trim(),
        p_workspace_type: "CUSTOM",
        p_parent_workspace_key: null,
      });
      if (error) throw error;
      const created = data as { workspace_key?: string; display_name?: string };
      setNewWorkspaceName("");
      setCreateOpen(false);
      await loadScope(selectedAccount);
      if (created?.workspace_key) setSelectedWorkspaceKey(created.workspace_key);
      setNotice(`${created?.display_name ?? "Workspace"} created. Connect a provider mount to activate files.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Workspace creation failed.");
    } finally {
      setCreatingWorkspace(false);
    }
  }

  const breadcrumbs = path ? path.split("/").filter(Boolean) : [];
  const capabilityList = selectedWorkspace?.capabilities ?? [];
  const selectedAccountEmail = systemAccountEmail
    ?? accounts.find((account) => account.user_id === selectedAccount)?.email
    ?? null;
  const fileSurfaceVisible = surface === "all" || surface === "files" || surface === "recent";
  const selectedAccountRole = accounts.find((account) => account.user_id === selectedAccount)?.role ?? role ?? "MEMBER";
  const selectedWorkspaceStatus = browseConnected ? `${activeBinding?.provider ?? "STORAGE"} · CONNECTED` : "STORAGE · NOT CONNECTED";

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <div className="border-b border-foreground/8 bg-[var(--top-bar)] px-3 py-2 sm:px-5">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-primary">RUNYI Workspace</p>
              <Badge variant="outline" className="h-4 border-primary/20 bg-primary/[0.06] px-1.5 text-[7px] text-primary">UWE v2</Badge>
            </div>
            <h1 className="truncate text-[15px] font-semibold leading-5">Universal Explorer</h1>
          </div>
          <div className="relative flex shrink-0 items-center gap-1">
            <Button type="button" variant="outline" size="icon-sm" className="size-7 rounded-lg" onClick={() => onNavigate("ai")} aria-label="Ask RUNYI AI"><Sparkles className="size-3.5" /></Button>
            <Button type="button" variant="outline" size="icon-sm" className="size-7 rounded-lg" onClick={() => { void loadScope(selectedAccount); void listObjects(true); void loadSystemObjects(); }} aria-label="Refresh"><RefreshCw className={`size-3.5 ${loading || scopeLoading || systemLoading ? "animate-spin" : ""}`} /></Button>
            <input ref={uploadRef} type="file" multiple className="hidden" onChange={(event) => void uploadFiles(event.target.files)} />
            <Button type="button" variant="outline" size="icon-sm" className="size-7 rounded-lg" disabled={!uploadEnabled || uploading} onClick={() => uploadRef.current?.click()} aria-label="Upload"><Upload className="size-3.5" /></Button>
            <Button type="button" variant="outline" size="icon-sm" className="size-7 rounded-lg" onClick={() => setMenuOpen((value) => !value)} aria-label="Workspace menu"><MoreVertical className="size-3.5" /></Button>
            {menuOpen && (
              <div className="absolute right-0 top-9 z-50 w-56 rounded-2xl border border-foreground/10 bg-popover p-1.5 shadow-2xl">
                <button type="button" onClick={() => { setMenuOpen(false); setCreateOpen(true); }} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs hover:bg-foreground/[0.06]"><Plus className="size-4" />New workspace</button>
                <button type="button" onClick={() => { setMenuOpen(false); onNavigate("bridge"); }} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs hover:bg-foreground/[0.06]"><Cloud className="size-4" />Provider connections</button>
                <button type="button" onClick={() => { setMenuOpen(false); onNavigate("ai"); }} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs hover:bg-foreground/[0.06]"><Sparkles className="size-4" />Ask RUNYI AI</button>
              </div>
            )}
          </div>
        </div>

        <button type="button" onClick={() => setScopeOpen((value) => !value)} className="mt-1.5 flex h-9 w-full items-center gap-2 rounded-lg border border-foreground/8 bg-background px-2.5 text-left">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[10px] font-semibold">{selectedWorkspace?.display_name ?? "Workspace"} · {selectedAccountEmail ?? "Account"}</span>
            <span className="block truncate text-[8px] text-foreground/38">{selectedAccountRole} · {selectedWorkspaceStatus} · {selectedWorkspace?.visibility ?? "PRIVATE"}</span>
          </span>
          <ChevronRight className={`size-3.5 shrink-0 text-foreground/35 transition-transform ${scopeOpen ? "rotate-90" : ""}`} />
        </button>

        {scopeOpen && (
          <div className="mt-1.5 grid gap-1.5 rounded-xl border border-foreground/8 bg-foreground/[0.015] p-2 sm:grid-cols-[minmax(0,1fr)_minmax(180px,.55fr)]">
            <label className="block min-w-0">
              <span className="mb-1 block text-[8px] font-bold uppercase tracking-[0.13em] text-foreground/35">Workspace</span>
              <select value={selectedWorkspaceKey ?? ""} onChange={(event) => setSelectedWorkspaceKey(event.target.value || null)} disabled={scopeLoading || workspaces.length === 0} className="h-8 w-full rounded-lg border border-foreground/10 bg-background px-2 text-[11px] outline-none focus:border-primary/50">
                {workspaces.map((workspace) => <option key={workspace.workspace_key} value={workspace.workspace_key}>{workspace.display_name} · {workspace.workspace_type}</option>)}
              </select>
            </label>
            {isAdmin && accounts.length > 0 && (
              <label className="block min-w-0">
                <span className="mb-1 block text-[8px] font-bold uppercase tracking-[0.13em] text-foreground/35">Account</span>
                <select value={selectedAccount ?? ""} onChange={(event) => { const value = event.target.value || null; setSelectedAccount(value); void loadScope(value); }} className="h-8 w-full rounded-lg border border-foreground/10 bg-background px-2 text-[10px] outline-none focus:border-primary/50">
                  {accounts.filter((account) => account.user_id).map((account) => <option key={account.user_id!} value={account.user_id!}>{account.email} · {account.role}</option>)}
                </select>
              </label>
            )}
          </div>
        )}
      </div>

      <div className="border-b border-foreground/8 px-3 py-2 sm:px-5">
        <div className="grid grid-cols-5 gap-0.5">
          {([
            ["all", "All", HardDrive],
            ["files", "Files", FolderOpen],
            ["infrastructure", "Infra", Cloud],
            ["apps", "Apps", Workflow],
            ["recent", "Recent", History],
          ] as const).map(([id,label,Icon]) => (
            <button key={id} type="button" onClick={() => setSurface(id)} className={`flex min-w-0 items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[9px] font-semibold transition ${surface === id ? "bg-foreground/10 text-foreground" : "text-foreground/45 hover:bg-foreground/[0.05]"}`}><Icon className="size-3.5 shrink-0" /><span className="truncate">{label}</span></button>
          ))}
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground/30" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search selected account…" className="h-8 rounded-lg border-foreground/10 bg-[var(--surface-inset)] pl-8 text-[12px]" />
          </div>
          {fileSurfaceVisible && <Button variant="outline" className="size-8 shrink-0 rounded-lg p-0" disabled={!uploadEnabled || uploading} onClick={() => uploadRef.current?.click()} aria-label="Upload">{uploading ? <LoaderCircle className="animate-spin" /> : <Upload />}</Button>}
        </div>
      </div>

      <div className="border-b border-foreground/8 bg-background px-3 py-1.5 sm:px-5">
        <button type="button" onClick={() => setResultsExpanded((value) => !value)} className="flex w-full items-center gap-2 text-left">
          <div className="min-w-0 flex-1">
            <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-primary">Universal account results</p>
            <p className="truncate text-[8px] text-foreground/38">{selectedAccountEmail || "Selected RUNYI account"} · governed scope</p>
          </div>
          <Badge className="shrink-0 bg-foreground/[0.05] px-2 py-0.5 text-[9px] text-foreground/55">{systemLoading ? "…" : systemTotal ?? "—"}</Badge>
          <ChevronRight className={`size-3.5 shrink-0 text-foreground/30 transition-transform ${resultsExpanded ? "rotate-90" : ""}`} />
        </button>
        {resultsExpanded && (
          <div className="mt-1.5">
            {systemError ? (
              <div className="rounded-lg border border-red-400/15 bg-red-400/[0.05] px-2.5 py-2 text-[10px] leading-4 text-red-300">
                Universal results unavailable: {systemError}
              </div>
            ) : (
              <div className={`${fileSurfaceVisible ? "max-h-[150px]" : "max-h-[260px]"} space-y-1 overflow-y-auto overscroll-contain pr-0.5`}>
                {systemObjects.map((item) => {
                  const itemEmail=item.identity_email || selectedAccountEmail || "NOT CONNECTED";
                  const provider=String(item.attributes?.provider_key ?? "");
                  const state = item.sync_state && item.sync_state !== "UNKNOWN"
                    ? item.sync_state
                    : item.health_state && item.health_state !== "UNKNOWN"
                      ? item.health_state
                      : item.lifecycle_state || "REGISTERED";
                  return (
                    <button key={item.entity_key} type="button" onClick={() => {
                      try { window.sessionStorage.setItem("runyi:universal-search-target",JSON.stringify({key:item.entity_key,title:item.display_name,route:routeForSystemObject(item),kind:"ENTITY"})); } catch {}
                      onNavigate(routeForSystemObject(item));
                    }} className="flex min-h-[48px] w-full items-center gap-2 rounded-lg border border-foreground/7 bg-foreground/[0.018] px-2 py-1.5 text-left hover:bg-foreground/[0.05]">
                      <span className="grid size-6 shrink-0 place-items-center rounded-md bg-foreground/[0.045] text-foreground/55">
                        {item.entity_type_key === "APPLICATION" || item.entity_type_key.includes("PROJECT") ? <Workflow className="size-3" /> : item.entity_type_key === "WORKSPACE" ? <FolderOpen className="size-3" /> : <Cloud className="size-3" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[10px] font-semibold">{item.display_name}</span>
                        <span className="mt-0.5 block truncate text-[8px] text-foreground/48">Email: {itemEmail}</span>
                        <span className="mt-0.5 block truncate text-[7px] text-foreground/32">{[provider,item.domain,state].filter(Boolean).join(" · ")}</span>
                      </span>
                      <ChevronRight className="size-3 shrink-0 text-foreground/25" />
                    </button>
                  );
                })}
                {!systemLoading && !systemError && systemObjects.length === 0 && <p className="px-2 py-3 text-[10px] text-foreground/40">No authorized system objects match this account and view.</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {fileSurfaceVisible ? (
      <div className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_290px]">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-8 items-center gap-1 overflow-x-auto border-b border-foreground/8 px-3 py-1 text-[10px] sm:px-5">
            <button type="button" onClick={() => setPath("")} className="flex min-w-max items-center gap-1 rounded-lg px-2 py-1 text-foreground/55 hover:bg-foreground/[0.05]"><HardDrive className="size-3.5" />{selectedWorkspace?.display_name ?? "Workspace"}</button>
            {breadcrumbs.map((part,index) => <div key={`${part}-${index}`} className="flex min-w-max items-center"><ChevronRight className="size-3 text-foreground/25" /><button type="button" onClick={() => setPath(breadcrumbs.slice(0,index+1).join("/"))} className="rounded-lg px-2 py-1 text-foreground/55 hover:bg-foreground/[0.05]">{part}</button></div>)}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-2.5 py-1.5 sm:px-5">
            {!connected ? (
              <Empty icon={ShieldCheck} title="RUNYI is not connected" detail="Sign in and restore the central control-plane connection before browsing workspaces." />
            ) : !catalogue ? (
              <Empty icon={LoaderCircle} title="Loading workspaces" detail="Resolving your permission-aware workspace catalogue." spinning />
            ) : !selectedWorkspace ? (
              <Empty icon={FolderOpen} title="No workspace available" detail="Create a workspace or ask an administrator to assign one." />
            ) : !browseConnected ? (
              <Empty icon={Cloud} title={`${selectedWorkspace.display_name} is not connected`} detail="This is a real governed workspace, but no active supported storage mount is attached yet. Connect a provider from Connections; RUNYI will not simulate file access." action={<Button variant="outline" onClick={() => onNavigate("bridge")}><Cloud />Provider connections</Button>} />
            ) : objects.length === 0 ? (
              <Empty
                icon={FolderOpen}
                title={loading ? "Workspace ready" : query ? "No matching objects" : "This location is empty"}
                detail={loading
                  ? "RUNYI is updating this location in the background. Navigation remains available."
                  : query
                    ? "Change the search term or workspace scope."
                    : "Upload a supported file or open another workspace."}
                action={!loading && uploadEnabled ? <Button variant="outline" onClick={() => uploadRef.current?.click()}><Upload />Upload files</Button> : undefined}
              />
            ) : (
              <div className="space-y-1">
                {objects.map((object) => {
                  const folder = isFolder(object);
                  const active = selected?.name === object.name && selected?.id === object.id;
                  return (
                    <button key={`${object.id ?? "folder"}-${object.name}`} type="button" onClick={() => folder ? setPath(joinPath(path,object.name)) : setSelected(object)} className={`grid min-h-[54px] w-full grid-cols-[30px_minmax(0,1fr)_auto] items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition ${active ? "border-primary/25 bg-primary/[0.06]" : "border-transparent hover:border-foreground/8 hover:bg-foreground/[0.035]"}`}>
                      <span className="grid size-7 place-items-center rounded-lg bg-foreground/[0.045] text-foreground/55">{folder ? <Folder className="size-4" /> : <File className="size-4" />}</span>
                      <span className="min-w-0"><span className="block truncate text-[12px] font-medium">{object.name}</span><span className="mt-0.5 block truncate text-[9px] text-foreground/35">{folder ? "Folder" : `${objectType(object)} · ${formatBytes(objectSize(object))} · ${formatDate(object.updated_at ?? object.created_at)}`}</span></span>
                      {folder ? <ChevronRight className="size-4 text-foreground/25" /> : downloadEnabled ? <span onClick={(event) => { event.stopPropagation(); void download(object); }} className="grid size-7 place-items-center rounded-lg text-foreground/40 hover:bg-foreground/[0.06] hover:text-foreground"><Download className="size-4" /></span> : null}
                    </button>
                  );
                })}
                {hasMore && <Button variant="outline" disabled={loading} onClick={() => void listObjects(false)} className="mt-3 w-full rounded-xl">{loading ? <LoaderCircle className="animate-spin" /> : null}Load more</Button>}
              </div>
            )}
          </div>
        </div>

        <aside className="hidden min-h-0 overflow-y-auto border-l border-foreground/8 bg-foreground/[0.015] p-4 lg:block">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-foreground/35">Inspector</p>
          {selected ? (
            <div className="mt-3 space-y-3">
              <div className="grid size-12 place-items-center rounded-xl border border-foreground/8 bg-background"><File className="size-5 text-foreground/55" /></div>
              <div><p className="break-words text-sm font-semibold">{selected.name}</p><p className="mt-1 text-[11px] text-foreground/40">{objectType(selected)} · {formatBytes(objectSize(selected))}</p></div>
              <InspectorRow label="Modified" value={formatDate(selected.updated_at ?? selected.created_at)} />
              <InspectorRow label="Workspace" value={selectedWorkspace?.display_name ?? "—"} />
              <InspectorRow label="Provider" value={activeBinding?.provider ?? "—"} />
              <InspectorRow label="Bucket" value={activeBinding?.bucket ?? "—"} />
              <InspectorRow label="Path" value={joinPath(activeBinding?.base_path,path,selected.name)} />
              <Button variant="outline" className="w-full rounded-xl" onClick={() => onNavigate("ai")}><Sparkles />Ask RUNYI AI</Button>
              <Button variant="outline" disabled={!downloadEnabled} className="w-full rounded-xl" onClick={() => void download(selected)}><Download />Secure download</Button>
            </div>
          ) : (
            <p className="mt-3 text-xs leading-5 text-foreground/38">Select an object to inspect provider, path and available actions.</p>
          )}
          <div className="mt-6 border-t border-foreground/8 pt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-foreground/35">Capabilities</p>
            <div className="mt-2 space-y-1.5">{capabilityList.map((item) => <div key={item.key} className="flex items-center justify-between gap-2 rounded-lg border border-foreground/6 bg-background/60 px-2.5 py-2"><span className="truncate text-[10px] font-medium text-foreground/55">{item.key.replaceAll("_"," ")}</span><Badge variant="outline" className={`h-5 shrink-0 px-1.5 text-[8px] ${capabilityBadge(item.state)}`}>{item.state.replaceAll("_"," ")}</Badge></div>)}</div>
          </div>
        </aside>
      </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5">
          <div className="grid min-h-[180px] place-items-center rounded-2xl border border-foreground/7 bg-foreground/[0.015] px-5 text-center">
            <div>
              <Cloud className="mx-auto size-5 text-foreground/35" />
              <p className="mt-2 text-xs font-semibold">{surface === "apps" ? "Applications & Projects view" : "Infrastructure & Services view"}</p>
              <p className="mt-1 text-[10px] leading-4 text-foreground/40">Use the universal account results above to open live RUNYI workspaces. Files stay available from the Files or All view.</p>
            </div>
          </div>
        </div>
      )}

      {notice && <div className="border-t border-foreground/8 bg-[var(--top-bar)] px-3 py-2 text-[11px] leading-4 text-foreground/55 sm:px-5"><Info className="mr-1 inline size-3.5" />{notice}</div>}

      {createOpen && (
        <div className="fixed inset-0 z-[100] grid place-items-end bg-black/55 p-3 sm:place-items-center">
          <div className="w-full max-w-md rounded-[24px] border border-foreground/10 bg-background p-5 shadow-2xl">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Universal workspace</p>
            <h2 className="mt-1 text-xl font-semibold">Create workspace</h2>
            <p className="mt-1 text-xs leading-5 text-foreground/45">The workspace is created immediately in the registry. File controls stay NOT CONNECTED until a provider mount is attached.</p>
            <Input autoFocus value={newWorkspaceName} onChange={(event) => setNewWorkspaceName(event.target.value)} placeholder="Workspace name" className="mt-4 h-11 rounded-xl" />
            <div className="mt-4 flex justify-end gap-2"><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button disabled={creatingWorkspace || !newWorkspaceName.trim()} onClick={() => void createWorkspace()}>{creatingWorkspace ? <LoaderCircle className="animate-spin" /> : <Plus />}Create</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function InspectorRow({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border border-foreground/7 bg-background/60 px-2.5 py-2"><p className="text-[9px] font-bold uppercase tracking-[0.12em] text-foreground/30">{label}</p><p className="mt-0.5 break-words text-[11px] leading-4 text-foreground/55">{value}</p></div>;
}

function Empty({ icon: Icon, title, detail, spinning, action }: { icon: typeof FolderOpen; title: string; detail: string; spinning?: boolean; action?: React.ReactNode }) {
  return <div className="grid min-h-[190px] place-items-center px-5 text-center"><div><div className="mx-auto grid size-12 place-items-center rounded-2xl border border-foreground/8 bg-foreground/[0.03] text-foreground/45"><Icon className={`size-5 ${spinning ? "animate-spin" : ""}`} /></div><p className="mt-3 text-sm font-semibold">{title}</p><p className="mx-auto mt-1 max-w-md text-xs leading-5 text-foreground/40">{detail}</p>{action ? <div className="mt-4 flex justify-center">{action}</div> : null}</div></div>;
}
